import type { ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Globe, Monitor, Moon, Mountain, Sun } from "lucide-react";
import { useAuth } from "../lib/auth";
import { gqlRequest } from "../lib/gql";
import {
  NAV_PERSON,
  OPEN_REPORT_COUNT,
  type NavPersonResult,
  type OpenReportCountResult,
} from "../lib/queries";
import { setThemeMode, useThemeMode, type ThemeMode } from "../lib/theme";
import { LOCALES, setLocale, useT, type Locale } from "../lib/i18n";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NotificationBell } from "./NotificationBell";
import { Avatar } from "./Avatar";
import { TabBar, TABS } from "./TabBar";

const THEME_CYCLE: Record<ThemeMode, ThemeMode> = { system: "light", light: "dark", dark: "system" };
const THEME_ICONS = { system: Monitor, light: Sun, dark: Moon } as const;
const THEME_KEYS = { system: "theme.system", light: "theme.light", dark: "theme.dark" } as const;

function ThemeToggle() {
  const { t } = useT();
  const mode = useThemeMode();
  const Icon = THEME_ICONS[mode];
  const next = THEME_CYCLE[mode];
  const current = t(THEME_KEYS[mode]);
  const nextLabel = t(THEME_KEYS[next]);
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => setThemeMode(next)}
      title={t("theme.toggleTitle", { current, next: nextLabel })}
      aria-label={t("theme.toggleAria", { current, next: nextLabel })}
      className="text-muted-foreground"
    >
      <Icon aria-hidden className="size-4.5" />
    </Button>
  );
}

// Native locale names — deliberately not translated.
const LOCALE_NAMES: Record<Locale, string> = { en: "English", de: "Deutsch", fr: "Français" };

/**
 * Globe + <select> locale switcher. `compact` renders the floating mobile
 * variant — the desktop header is hidden below `sm`, and (unlike the theme
 * toggle) the language switch must stay reachable on phones.
 */
export function LocaleSwitcher({ compact = false }: { compact?: boolean }) {
  const { t, locale } = useT();
  return (
    <label
      className={
        compact
          ? "fixed bottom-[4.25rem] right-3 z-30 flex items-center gap-1 rounded-full border bg-background/90 px-2.5 py-1.5 shadow-card backdrop-blur-md sm:hidden"
          : "flex items-center gap-1 rounded-lg px-1.5 text-muted-foreground transition-colors hover:bg-accent"
      }
    >
      <Globe aria-hidden className="size-4 shrink-0" />
      <select
        value={locale}
        aria-label={t("locale.label")}
        onChange={(e) => setLocale(e.target.value as Locale)}
        className="cursor-pointer appearance-none bg-transparent py-1.5 pr-1 text-sm font-medium text-foreground focus:outline-none"
      >
        {LOCALES.map((l) => (
          <option key={l} value={l}>
            {LOCALE_NAMES[l]}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Nav link to the moderation queue, with a badge for open reports. Rendered only for admins. */
function ModerationLink({ active }: { active: boolean }) {
  const { t } = useT();
  const { data } = useQuery({
    queryKey: ["openReportCount"],
    queryFn: () => gqlRequest<OpenReportCountResult>(OPEN_REPORT_COUNT),
  });
  const openCount = data?.contentReports.totalCount ?? 0;
  return (
    <Link
      href="/admin/reports"
      aria-current={active ? "page" : undefined}
      className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? "bg-primary/10 text-primary"
          : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
      }`}
    >
      {t("nav.moderation")}
      {openCount > 0 && (
        <Badge variant="destructive" className="px-1.5 font-semibold tabular-nums">
          {openCount}
        </Badge>
      )}
    </Link>
  );
}

/** Header avatar button opening a small dropdown (profile link, log out). */
function UserMenu({ personId }: { personId: string }) {
  const { t } = useT();
  const { logout } = useAuth();
  const [, navigate] = useLocation();

  const { data } = useQuery({
    queryKey: ["navPerson", personId],
    queryFn: () => gqlRequest<NavPersonResult>(NAV_PERSON, { id: personId }),
  });
  const person = data?.person;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t("nav.myProfile")}
          className="flex items-center rounded-full p-0.5 transition-opacity hover:opacity-80"
        >
          <Avatar person={{ displayName: person?.displayName ?? null, avatarKey: person?.avatarKey }} size="md" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-44">
        <DropdownMenuItem asChild>
          <Link href="/profile">{t("nav.profile")}</Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => {
            logout();
            navigate("/");
          }}
        >
          {t("profile.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const { personId, isAdmin } = useAuth();
  const { t } = useT();
  const isMap = location === "/";

  return (
    <div className="flex h-full flex-col">
      {/* Desktop / tablet top bar */}
      <header className="sticky top-0 z-30 hidden items-center gap-8 border-b bg-background/80 px-5 py-2.5 backdrop-blur-md sm:flex">
        <Link
          href="/"
          className="flex items-center gap-2 text-base font-semibold tracking-tight text-foreground transition-opacity hover:opacity-80"
        >
          <Mountain aria-hidden className="size-5 text-primary" />
          gaea
        </Link>
        <nav className="flex gap-0.5">
          {TABS.filter((tab) => tab.href !== "/profile").map((tab) => {
            const active = tab.href === "/" ? location === "/" : location.startsWith(tab.href);
            return (
              <Link
                key={tab.href}
                href={tab.href}
                aria-current={active ? "page" : undefined}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  }`}
              >
                {t(tab.labelKey)}
              </Link>
            );
          })}
          {isAdmin && <ModerationLink active={location.startsWith("/admin")} />}
        </nav>
        <div className="ml-auto flex items-center gap-1">
          <LocaleSwitcher />
          <ThemeToggle />
          {personId ? (
            <>
              <NotificationBell />
              <UserMenu personId={personId} />
            </>
          ) : (
            <Button asChild size="sm" className="font-semibold shadow-card hover:shadow-card-hover">
              <Link href="/login">{t("common.logIn")}</Link>
            </Button>
          )}
        </div>
      </header>

      {/* pb keeps content clear of the mobile tab bar; the map page manages its own height */}
      <main className={`min-h-0 flex-1 ${isMap ? "" : "overflow-y-auto pb-16 sm:pb-0"}`}>{children}</main>

      {/* Mobile-only floating switcher — the header above is hidden below sm. */}
      <LocaleSwitcher compact />

      <TabBar />
    </div>
  );
}
